import { createContext, useContext, useEffect, useState, type ReactNode } from "react"

type ThemeMode = "light" | "dark" | "auto"
type ResolvedTheme = "light" | "dark"

interface ThemeContextType {
  mode: ThemeMode
  resolvedTheme: ResolvedTheme
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Kept in sync with the literal in hydrateTheme, which must stay self-contained
// to be inlined as a script.
export const THEME_STORAGE_KEY = "theme-mode"

export function hydrateTheme() {
  try {
    const mode = localStorage.getItem("theme-mode")
    const isDark =
      mode === "dark" || (mode !== "light" && matchMedia("(prefers-color-scheme:dark)").matches)
    if (isDark) document.documentElement.classList.add("dark")
  } catch {}
}

export const themeHydrationScript = `(${hydrateTheme.toString()})()`

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light"
  // Fallback for test environments without matchMedia
  if (!window.matchMedia) return "light"
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function getStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === "light" || stored === "dark" || stored === "auto") {
      return stored
    }
  } catch {
    // Ignore localStorage errors
  }
  return "auto"
}

export function ThemeProvider({ children }: { children: ReactNode; isAuthenticated?: boolean }) {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode)
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme)

  // Use mode setting for theme resolution
  const resolvedTheme: ResolvedTheme = mode === "auto" ? systemTheme : mode

  useEffect(() => {
    // Skip media query listener in test environments without matchMedia
    if (!window.matchMedia) return

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light")
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove("light", "dark")
    root.classList.add(resolvedTheme)
  }, [resolvedTheme])

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newMode)
    } catch {
      // Ignore localStorage errors
    }
  }

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider")
  }
  return context
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalTheme() {
  return (
    useContext(ThemeContext) ?? {
      mode: "light" as const,
      resolvedTheme: "light" as const,
      setMode: () => {},
    }
  )
}
